import React, { useState } from 'react';
import { Container as ContainerType } from '../../../types/canvas.types';
import { AddNestedHorizontal } from './AddNestedHorizontal';
import { ContainerHeader } from '../Container/ContainerHeader';
import ContainerContent from '../Container/ContainerContent';
import { useEditor } from '../../../contexts/EditorContext';

const MAX_NESTING_LEVEL = 2;

interface CollapsibleNestedContainerProps {
    id?: string;
    nestedContainer: ContainerType;
    nestingLevel: number;
    selectedContainerId: string | null;
    selectedFieldId?: string | null;
    selectedSubmodelId?: string | null;
    getContainerHighlightClass: (container: ContainerType) => string;
    getWidthStyle: (container: ContainerType) => any;
    getAlignmentClass: (alignment: 'left' | 'center' | 'right') => string;
    handleDirectNestedContainerSelect: (containerId: string, e: React.MouseEvent) => void;
    handleRemoveComponent: (componentId: string, containerId: string) => void;
    onFieldSelect?: (fieldId: string, fieldType: string, containerId: string, parentId?: string) => void;
    onSubmodelSelect?: (submodelId: string, submodelName: string) => void;
    onWidgetSelect?: (widgetId: string, widgetType: string, configuration?: any) => void;
    onContentUpdate?: (widgetId: string, content: string) => void;
    onAddContainer?: (afterContainerId: string) => void;
    onDuplicateContainer?: (containerId: string) => void;
    onDeleteContainer?: (containerId: string) => void;
    onSelect: (containerId: string) => void;
    onAddNestedHorizontal: (parentId: string) => void;
    onAddNestedVertical: (parentId: string, rowIndex?: number) => void;
    onAddNestedBetween?: (parentId: string, beforeIndex?: number) => void;
    onRemoveComponent?: (componentId: string, containerId: string) => void;
}

export const CollapsibleNestedContainer: React.FC<CollapsibleNestedContainerProps> = ({
    id,
    nestedContainer,
    nestingLevel,
    selectedContainerId,
    selectedFieldId,
    selectedSubmodelId,
    getContainerHighlightClass,
    getWidthStyle,
    getAlignmentClass,
    handleDirectNestedContainerSelect,
    handleRemoveComponent,
    onFieldSelect,
    onSubmodelSelect,
    onWidgetSelect,
    onContentUpdate,
    onAddContainer,
    onDuplicateContainer,
    onDeleteContainer,
    onSelect,
    onAddNestedHorizontal,
    onAddNestedVertical,
    onAddNestedBetween,
    onRemoveComponent
}) => {
    const baseId = id || 'collapsible-nested-container';
    const { editorState } = useEditor();
    const [isCollapsed, setIsCollapsed] = useState(false);

    const containerCanHaveNested = nestingLevel < MAX_NESTING_LEVEL;

    const NestedContainer = require('./NestedContainer').NestedContainer;

    return (
        <div
            id={baseId}
            data-testid={baseId}
            className={`tw-border tw-mb-1 tw-flex tw-flex-col ${getContainerHighlightClass(nestedContainer)}`}
            style={getWidthStyle(nestedContainer)}
            onClick={(e) => handleDirectNestedContainerSelect(nestedContainer.id, e)}
            data-container-type="nested"
            data-container-id={nestedContainer.id}
        >
            {nestedContainer.configuration.showTitle && (
                <ContainerHeader
                    id={`${baseId}-header`}
                    name={nestedContainer.name}
                    containerId={nestedContainer.id}
                    componentId={nestedContainer.content.componentId}
                    containerType={nestedContainer.content.type}
                    widgetType={nestedContainer.content['widget-type']}
                    hasNestedContainers={nestedContainer.content.nestedContainers && nestedContainer.content.nestedContainers.length > 0}
                    nestingLevel={nestingLevel}
                    onRemoveComponent={(componentId) => handleRemoveComponent(componentId, nestedContainer.id)}
                    onAddContainer={() => onAddContainer?.(nestedContainer.id)}
                    onDuplicateContainer={() => onDuplicateContainer?.(nestedContainer.id)}
                    onDeleteContainer={() => onDeleteContainer?.(nestedContainer.id)}
                    collapsible={nestedContainer.configuration.collapsible || false}
                    isCollapsed={isCollapsed}
                    onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
                />
            )}

            {!isCollapsed && (
                <div
                    id={`${baseId}-content-wrapper`}
                    className={`tw-overflow-auto tw-flex ${getAlignmentClass(nestedContainer.configuration.alignment) === 'tw-mx-auto' ? 'tw-justify-center' : getAlignmentClass(nestedContainer.configuration.alignment) === 'tw-ml-auto' ? 'tw-justify-end' : ''}`}
                    style={{
                        width: '100%',
                        height: nestedContainer.configuration.height !== 'auto' ?
                            nestedContainer.configuration.height : undefined
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div id={`${baseId}-content-area`} className={`tw-p-2`}>
                        <div id={`${baseId}-content-layout`} className={`${nestedContainer.configuration.layout === 'horizontal' ? 'tw-flex tw-flex-row tw-flex-wrap tw-gap-4' : ''}`}>
                            <ContainerContent
                                id={`${baseId}-container-content`}
                                componentId={nestedContainer.content.componentId}
                                usedComponents={nestedContainer.usageInfo?.usedComponents}
                                items={nestedContainer.content.items}
                                submodel={nestedContainer.content.submodel}
                                containerConfig={nestedContainer.configuration}
                                onFieldSelect={(fieldId, fieldType, parentId) => {
                                    if (onFieldSelect) {
                                        onFieldSelect(fieldId, fieldType, nestedContainer.id, parentId);
                                    }
                                }}
                                selectedFieldId={selectedFieldId}
                                onSubmodelSelect={(submodelId) => {
                                    if (onSubmodelSelect) {
                                        const submodelName = nestedContainer.content.submodel?.name || 'Unknown Submodel';
                                        onSubmodelSelect(submodelId, submodelName);
                                    }
                                }}
                                selectedSubmodelId={editorState.selectedSubmodelId}
                                onWidgetSelect={onWidgetSelect}
                                selectedWidgetId={editorState.selectedWidgetId}
                                onContentUpdate={onContentUpdate}
                                isNested={true}
                            />
                        </div>
                    </div>

                    {containerCanHaveNested && (
                        <div id={`${baseId}-nested-section`} className="tw-px-2">
                            {(!nestedContainer.content.nestedContainers || nestedContainer.content.nestedContainers.length === 0) ? (
                                <AddNestedHorizontal
                                    id={`${baseId}-add-nested-horizontal`}
                                    parentId={nestedContainer.id}
                                    onAdd={() => {
                                        console.log(`Adding nested container to ${nestedContainer.id}`);
                                        onAddNestedHorizontal(nestedContainer.id);
                                    }}
                                />
                            ) : (
                                <NestedContainer
                                    id={`${baseId}-nested-container`}
                                    parentId={nestedContainer.id}
                                    nestedContainers={nestedContainer.content.nestedContainers}
                                    selectedContainerId={selectedContainerId}
                                    onSelect={(nestedId: string) => {
                                        onSelect(nestedId);
                                    }}
                                    onAddNestedHorizontal={onAddNestedHorizontal}
                                    onAddNestedVertical={onAddNestedVertical}
                                    onAddNestedBetween={onAddNestedBetween}
                                    onRemoveComponent={onRemoveComponent}
                                    nestingLevel={nestingLevel + 1}
                                    containerConfig={nestedContainer.configuration}
                                    onFieldSelect={onFieldSelect}
                                    selectedFieldId={selectedFieldId}
                                    onSubmodelSelect={onSubmodelSelect}
                                    selectedSubmodelId={selectedSubmodelId}
                                    onWidgetSelect={onWidgetSelect}
                                    onContentUpdate={onContentUpdate}
                                    onAddContainer={onAddContainer}
                                    onDuplicateContainer={onDuplicateContainer}
                                    onDeleteContainer={onDeleteContainer}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
